'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import {
  formatJsonRpcError,
  formatJsonRpcResult,
} from '@walletconnect/jsonrpc-utils'
import { SessionTypes } from '@walletconnect/types'
import {
  SdkErrorKey,
  buildApprovedNamespaces,
  getSdkError,
} from '@walletconnect/utils'
import { keyBy, omitBy, uniqBy } from 'lodash'
import { useLocalStorage } from 'react-use'
import { toast } from 'sonner'
import { Hex, hexToBigInt, toHex } from 'viem'

import {
  AcceptWalletConnectMessageDialogContent,
  AcceptWalletConnectTransactionDialogContent,
  WalletConnectAppIcon,
} from '~/components/smart-accounts/SmartAccountWalletConnect'
import { SupportedChainId } from '~/constants/chains'
import { DEFAULT_TXN_HASH } from '~/constants/smart_accounts'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org'
import { CreateTransactionData } from '~/hooks/smart-account-actions/createTransactionData.type'
import { CreateMessageData } from '~/hooks/smart-accounts/useMessageActions'
import { useGetTransaction } from '~/hooks/useGetTransaction'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  BasicTransactionType,
  OrgMessage,
  OrgTransaction,
  SmartAccountType,
} from '~/types'
import { customStringify, getDomainFromUrl } from '~/utils'
import { EvmAddress } from '~/utils/address'
import { addSentryBreadcrumb, captureException } from '~/utils/errors'
import {
  EIP155_PREFIX,
  extractNamespaceChains,
  getDeployCreateData,
} from '~/utils/smartAccounts'
import { operationIdToHex } from '~/utils/smartAccounts/operationId'
import { getWalletConnectCallStatus } from '~/utils/walletConnect'
import { categorizeMissingChains } from '~/utils/walletConnectChains'

import type { IWalletKit, WalletKitTypes } from '@reown/walletkit'

enum RequestMethod {
  ETH_SENDTRANSACTION = 'eth_sendTransaction',
  PERSONAL_SIGN = 'personal_sign',
  ETH_SIGNTYPEDDATA_V4 = 'eth_signTypedData_v4',
  GET_CAPABILITIES = 'wallet_getCapabilities',
  SEND_CALLS = 'wallet_sendCalls',
  GET_CALLS_STATUS = 'wallet_getCallsStatus',
}

const walletConnectProjectId = process.env.WALLETCONNECT_PROJECT_ID

type Eip5792Capabilities = {
  // Legacy key — Uniswap and Relay still gate wallet_sendCalls on this
  // rather than the newer `atomic.status` field.
  atomicBatch: { supported: true }
  atomic: { status: 'supported' }
  paymasterService: { status: 'supported' }
}

const EIP_5792_METHODS: readonly string[] = [
  RequestMethod.GET_CAPABILITIES,
  RequestMethod.SEND_CALLS,
  RequestMethod.GET_CALLS_STATUS,
]

const buildEip5792CapabilitiesByChainId = (
  chainIds: number[],
): Record<Hex, Eip5792Capabilities> =>
  chainIds.reduce<Record<Hex, Eip5792Capabilities>>((acc, chainId) => {
    acc[toHex(chainId)] = {
      atomicBatch: { supported: true },
      atomic: { status: 'supported' },
      paymasterService: { status: 'supported' },
    }
    return acc
  }, {})

const useWeb3Wallet = () => {
  const [web3wallet, setWeb3Wallet] = useState<IWalletKit | undefined>()
  const isLoadingRef = useRef(false)

  const initWalletConnect = useCallback(async () => {
    if (!walletConnectProjectId)
      throw new Error('Wallet connect project id required')

    if (!!web3wallet || isLoadingRef.current) return

    if (typeof window === 'undefined' || !('indexedDB' in window)) return

    isLoadingRef.current = true
    try {
      // Dynamic imports to avoid SSR issues with indexedDB
      const [{ Core }, { WalletKit }] = await Promise.all([
        import('@walletconnect/core'),
        import('@reown/walletkit'),
      ])

      // customStoragePrefix keeps this wallet-side core out of the shared
      // global slot: without it, new Core() adopts whichever core Privy/wagmi
      // already registered on globalThis — sharing a different-version core's
      // internals and heartbeat (the "Core is already initialized" and
      // MaxListenersExceededWarning consoles) and colliding wc@2 storage keys.
      const core = new Core({
        projectId: walletConnectProjectId,
        customStoragePrefix: 'walletkit',
      })
      // Our stack alone puts 7 listeners on the heartbeat, and relay
      // reconnects add transient ones — give headroom over the default of 10.
      core.heartbeat.events.setMaxListeners(20)

      const newWeb3Wallet = await WalletKit.init({
        core,
        metadata: {
          name: 'Splits Smart Accounts',
          description: 'Passkey-based multisig smart accounts for teams',
          url: window.location.origin,
          icons: [`${window.location.origin}/logo_light.png`],
        },
      })
      setWeb3Wallet(newWeb3Wallet)
    } finally {
      isLoadingRef.current = false
    }
  }, [web3wallet, isLoadingRef])

  return {
    web3wallet,
    initWalletConnect,
  }
}

type ApproveSessionFunction = (
  address: EvmAddress.Type,
  chainIds: number[],
  proposal: WalletKitTypes.SessionProposal,
) => Promise<void>
type RejectSessionFunction = (
  proposal: WalletKitTypes.SessionProposal,
  reason?: SdkErrorKey,
) => Promise<void>

const useSessionProposal = (
  web3wallet: IWalletKit | undefined,
): {
  approveSession: ApproveSessionFunction
  rejectSession: RejectSessionFunction
  removeSession: (topic: string) => void
  onSessionProposal: (proposal: WalletKitTypes.SessionProposal) => Promise<void>
  addSession: (newSession: SessionTypes.Struct) => void
  setIsPairing: (isPairing: boolean) => void
  pendingProposal: WalletKitTypes.SessionProposal | undefined
  isApproving: boolean
  activeSessions: SessionTypes.Struct[]
  isPairing: boolean
} => {
  const queryClient = useQueryClient()
  const [isPairing, setIsPairing] = useState(false)
  const [pendingProposal, setPendingProposal] = useState<
    WalletKitTypes.SessionProposal | undefined
  >()
  const [isApproving, setIsApproving] = useState(false)

  const [activeSessions, setActiveSessions] = useState<SessionTypes.Struct[]>(
    [],
  )

  const addSession = useCallback((newSession: SessionTypes.Struct) => {
    setActiveSessions((sessions) => {
      return uniqBy([...sessions, newSession], 'topic')
    })
  }, [])

  const removeSession = useCallback(
    (topicToRemove: string) => {
      setActiveSessions(
        activeSessions.filter((session) => session.topic !== topicToRemove),
      )
    },
    [activeSessions],
  )

  const rejectSession = useCallback(
    async (
      proposal: WalletKitTypes.SessionProposal,
      reason: SdkErrorKey | undefined,
    ) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      try {
        await web3wallet.rejectSession({
          id: proposal.id,
          reason: getSdkError(reason ?? 'USER_REJECTED'),
        })
      } catch (error) {
        // Likely this happens because the proposal expired,
        // so we don't need to do anything, but we'll log to
        // Sentry just in case
        captureException(error)
      }

      setPendingProposal(undefined)
    },
    [web3wallet],
  )

  const approveSession = useCallback(
    async (
      address: EvmAddress.Type,
      chainIds: number[],
      proposal: WalletKitTypes.SessionProposal,
    ) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      try {
        setIsApproving(true)
        const eip155ChainStringList = chainIds.map(
          (chainId) => `${EIP155_PREFIX}${chainId}`,
        )
        const accountsList = eip155ChainStringList.reduce(
          (accAccountsList, chainString) => {
            const accountChainList = [`${chainString}:${address.toLowerCase()}`]
            return accAccountsList.concat(accountChainList)
          },
          [] as string[],
        )

        let approvedNamespaces
        try {
          approvedNamespaces = buildApprovedNamespaces({
            proposal: proposal.params,
            supportedNamespaces: {
              eip155: {
                chains: eip155ChainStringList,
                methods: [
                  RequestMethod.ETH_SENDTRANSACTION,
                  RequestMethod.PERSONAL_SIGN,
                  RequestMethod.ETH_SIGNTYPEDDATA_V4,
                  RequestMethod.GET_CAPABILITIES,
                  RequestMethod.SEND_CALLS,
                  RequestMethod.GET_CALLS_STATUS,
                ],
                events: ['accountsChanged', 'chainChanged'],
                accounts: accountsList,
              },
            },
          })
        } catch (e) {
          const requiredChainIds = extractNamespaceChains(
            proposal.params.requiredNamespaces,
          )
          const missingChainIds = requiredChainIds.filter(
            (requiredChainId) => !chainIds.includes(requiredChainId),
          )

          const {
            hasUnsupportedByTeamsChains,
            hasNotEnabledOnOrgChains,
            unsupportedByTeamsChainNames,
            notEnabledOnOrgChainNames,
          } = categorizeMissingChains(missingChainIds, queryClient)

          let errorMessage: string
          if (hasUnsupportedByTeamsChains) {
            errorMessage = `Unable to connect! This app requires ${unsupportedByTeamsChainNames.join(', ')} which Splits does not support.`
          } else if (hasNotEnabledOnOrgChains) {
            errorMessage = `Unable to connect! This app requires ${notEnabledOnOrgChainNames.join(', ')}. Enable ${
              notEnabledOnOrgChainNames.length > 1
                ? 'these networks'
                : 'this network'
            } in Settings to connect.`
          } else {
            errorMessage =
              'Error connecting! Please try again, or reach out to support@splits.org for assistance'
          }
          toast.error(errorMessage, {
            duration: 7000,
          })
          captureException(e)
          await rejectSession(proposal, 'UNSUPPORTED_CHAINS')
          return
        }

        if (
          !approvedNamespaces.eip155.chains ||
          approvedNamespaces.eip155.chains.length === 0
        ) {
          toast.error(
            `Failed to connect to ${proposal.params.proposer.metadata.name || proposal.params.proposer.metadata.url}`,
            {
              description: 'No networks are supported',
            },
          )
          await rejectSession(proposal, 'UNSUPPORTED_CHAINS')
          return
        }

        const approvedChainIds = extractNamespaceChains({
          eip155: approvedNamespaces.eip155,
        })

        approvedNamespaces.eip155.methods = Array.from(
          new Set([
            ...(approvedNamespaces.eip155.methods ?? []),
            ...EIP_5792_METHODS,
          ]),
        )

        const newSession = await web3wallet.approveSession({
          id: proposal.id,
          namespaces: approvedNamespaces,
          sessionProperties: {
            capabilities: JSON.stringify({
              [address.toLowerCase()]:
                buildEip5792CapabilitiesByChainId(approvedChainIds),
            }),
          },
        })

        addSession(newSession)
        toast.success(
          `Connected to ${
            newSession.peer.metadata.name || newSession.peer.metadata.url
          }`,
        )
      } catch (error) {
        toast.error('Error connecting')
        captureException(error)

        await rejectSession(proposal, 'SESSION_SETTLEMENT_FAILED')
      } finally {
        setIsApproving(false)
        setPendingProposal(undefined)
      }
    },
    [web3wallet, addSession, rejectSession, queryClient],
  )

  const onSessionProposal = useCallback(
    async (proposal: WalletKitTypes.SessionProposal) => {
      setPendingProposal(proposal)
      setIsPairing(false)
    },
    [],
  )

  return {
    approveSession,
    rejectSession,
    removeSession,
    onSessionProposal,
    addSession,
    setIsPairing,
    isApproving,
    pendingProposal,
    activeSessions,
    isPairing,
  }
}

const useOnSessionRequest = (
  web3wallet: IWalletKit | undefined,
  activeSessions: SessionTypes.Struct[],
): {
  onSessionRequest: (event: WalletKitTypes.SessionRequest) => Promise<void>
  respondWithSignature: (message: OrgMessage) => Promise<void>
  respondWithCanceled: (
    data: CreateMessageData | CreateTransactionData,
  ) => Promise<void>
  respondWithTransactionHash: (transaction: OrgTransaction) => Promise<void>
  hasSubmittedMessage: (messageId: string) => boolean
} => {
  const { addNewMessageId, lookupMessageId } = useStoreSubmittedMessages()
  const { addNewRequestId, lookupRequestId } = useStoreRequestIds()
  const { smartAccounts } = useSmartAccounts()
  const { getTransaction } = useGetTransaction()

  const { org } = useActiveOrg()
  const smartAccountsByAddress = useMemo(() => {
    return keyBy(smartAccounts, 'address')
  }, [smartAccounts])

  const respondWithCanceled = useCallback(
    async (data: CreateMessageData | CreateTransactionData) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')
      if (
        !data.walletConnectSessionRequestId ||
        !data.walletConnectSessionRequestTopic
      )
        throw new Error('Request id and topic required')

      const response = formatJsonRpcError(
        data.walletConnectSessionRequestId,
        'User canceled request.',
      )
      await web3wallet.respondSessionRequest({
        topic: data.walletConnectSessionRequestTopic,
        response,
      })
    },
    [web3wallet],
  )

  const respondWithSignature = useCallback(
    async (message: OrgMessage) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      if (
        !message.walletConnectSessionRequestId ||
        !message.walletConnectSessionRequestTopic
      )
        throw new Error(`Message missing id or topic: ${message.id}`)

      const response = formatJsonRpcResult(
        parseInt(message.walletConnectSessionRequestId),
        message.signature,
      )

      await web3wallet.respondSessionRequest({
        topic: message.walletConnectSessionRequestTopic,
        response,
      })

      toast.success('Message sent', {
        id: message.id,
      })
      addNewMessageId(message.id)
    },
    [addNewMessageId, web3wallet],
  )

  const respondWithTransactionHash = useCallback(
    async (transaction: OrgTransaction) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      if (
        !transaction.walletConnectSessionRequestId ||
        !transaction.walletConnectSessionRequestTopic
      )
        throw new Error(`Message missing id or topic: ${transaction.id}`)

      if (
        !activeSessions.find(
          (s) => s.topic === transaction.walletConnectSessionRequestTopic,
        )
      )
        return

      const response = formatJsonRpcResult(
        parseInt(transaction.walletConnectSessionRequestId),
        transaction.transactionHash ?? DEFAULT_TXN_HASH,
      )

      await web3wallet.respondSessionRequest({
        topic: transaction.walletConnectSessionRequestTopic,
        response,
      })
    },
    [activeSessions, web3wallet],
  )

  const respondWithTransactionId = useCallback(
    async (transaction: OrgTransaction) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      if (
        !transaction.walletConnectSessionRequestId ||
        !transaction.walletConnectSessionRequestTopic
      )
        throw new Error(`Message missing id or topic: ${transaction.id}`)

      if (
        !activeSessions.find(
          (s) => s.topic === transaction.walletConnectSessionRequestTopic,
        )
      )
        return

      const operationId = transaction.batchOperations?.[0]?.operationId
      const responseId = operationId
        ? operationIdToHex(operationId)
        : transaction.id

      const response = formatJsonRpcResult(
        parseInt(transaction.walletConnectSessionRequestId),
        {
          id: responseId,
        },
      )

      await web3wallet.respondSessionRequest({
        topic: transaction.walletConnectSessionRequestTopic,
        response,
      })
    },
    [activeSessions, web3wallet],
  )

  const respondWithBatchResult = useCallback(
    async ({
      requestId,
      requestTopic,
      result,
    }: {
      requestId: number
      requestTopic: string
      result: unknown
    }) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')
      if (!activeSessions.find((s) => s.topic === requestTopic)) return

      await web3wallet.respondSessionRequest({
        topic: requestTopic,
        response: formatJsonRpcResult(requestId, result),
      })
    },
    [activeSessions, web3wallet],
  )

  const respondWithCapabilities = useCallback(
    async ({
      chainIds,
      requestId,
      requestTopic,
    }: {
      chainIds: number[]
      requestId: number
      requestTopic: string
    }) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      if (!requestId || !requestTopic)
        throw new Error(`Message missing id or topic: ${requestId}`)

      const response = formatJsonRpcResult(
        requestId,
        buildEip5792CapabilitiesByChainId(chainIds),
      )

      await web3wallet.respondSessionRequest({
        topic: requestTopic,
        response,
      })
    },
    [web3wallet],
  )

  const respondWithCallsStatus = useCallback(
    async ({
      transactionId,
      requestId,
      requestTopic,
      requestChainId,
    }: {
      transactionId: string
      requestId: number
      requestTopic: string
      requestChainId: number
    }) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      if (!requestId || !requestTopic)
        throw new Error(`Message missing id or topic: ${requestId}`)

      const response = await getWalletConnectCallStatus(
        getTransaction,
        transactionId,
        requestChainId,
      )

      await web3wallet.respondSessionRequest({
        topic: requestTopic,
        response: formatJsonRpcResult(requestId, response),
      })
    },
    [web3wallet, getTransaction],
  )

  const hasSubmittedMessage = useCallback(
    (messageId: string) => {
      return lookupMessageId(messageId)
    },
    [lookupMessageId],
  )

  const { setGlobalModalState, closeGlobalModal } = useAuth()

  const onSessionRequest = useCallback(
    async (event: WalletKitTypes.SessionRequest) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      const { topic, params, id } = event
      const { request } = params
      const requestParamsMessage = request.params[0]
      const chainId = parseInt(
        params.chainId.slice(EIP155_PREFIX.length),
      ) as SupportedChainId

      if (request.method === RequestMethod.GET_CALLS_STATUS) {
        respondWithCallsStatus({
          transactionId: requestParamsMessage,
          requestId: id,
          requestTopic: topic,
          requestChainId: chainId,
        })

        return
      }

      const address =
        request.method === RequestMethod.ETH_SIGNTYPEDDATA_V4 ||
        request.method === RequestMethod.GET_CAPABILITIES
          ? EvmAddress.format(request.params[0])
          : request.method === RequestMethod.PERSONAL_SIGN
            ? EvmAddress.format(request.params[1])
            : EvmAddress.format(requestParamsMessage.from)

      const smartAccount = smartAccountsByAddress[address]
      if (!smartAccount) return

      // Prevent duplicate requests on hard refresh, wallet connect tries to resubmit
      const isProcessed = lookupRequestId(topic, id)
      if (isProcessed) return

      const session = activeSessions.find((s) => s.topic === topic)
      const appName = session?.peer.metadata.name ?? session?.peer.metadata.url
      const appUrl = session?.peer.metadata.url
      const appDomain = appUrl ? getDomainFromUrl(appUrl) : undefined
      const appIcon = session?.peer.metadata.icons?.[0]

      if (request.method === RequestMethod.GET_CAPABILITIES && org) {
        respondWithCapabilities({
          chainIds: org.smartAccountsChainIds,
          requestId: id,
          requestTopic: topic,
        })

        return
      }

      if (
        request.method === RequestMethod.PERSONAL_SIGN ||
        request.method === RequestMethod.ETH_SIGNTYPEDDATA_V4
      ) {
        const dataType =
          request.method.toUpperCase() as CreateMessageData['dataType']
        const dataToSign =
          request.method === RequestMethod.PERSONAL_SIGN
            ? requestParamsMessage
            : request.params[1]
        const messageData: CreateMessageData = {
          chainId,
          name: `Sign message request for ${appName}`,
          dataToSign,
          dataType,
          smartAccountId: smartAccount.id,
          walletConnectAppIcon: appIcon,
          walletConnectAppName: appName,
          walletConnectSessionRequestId: id,
          walletConnectSessionRequestTopic: topic,
        }
        setGlobalModalState({
          isOpen: true,
          content: (
            <AcceptWalletConnectMessageDialogContent
              smartAccount={smartAccount}
              data={messageData}
              icon={
                <WalletConnectAppIcon
                  appIcon={appIcon}
                  appName={appName}
                  size={56}
                />
              }
              onClose={() => {
                setGlobalModalState({ isOpen: false, content: null })
              }}
              onAccept={() => {
                addNewRequestId(topic, id)
              }}
              onError={() => {
                addSentryBreadcrumb(customStringify(requestParamsMessage))
              }}
            />
          ),
          onDismiss: () => {
            respondWithCanceled(messageData)
          },
        })
        return
      }

      if (request.method === RequestMethod.SEND_CALLS) {
        const calls: { to: string; data: string; value: string }[] =
          request.params[0].calls // value is hex

        const finalCalls = calls.map((call) => ({
          to: EvmAddress.format(call.to),
          data: call.data as Hex,
          value: hexToBigInt(call.value ? (call.value as Hex) : '0x0'),
        }))

        const appDomain = session?.peer.metadata.url
          ? getDomainFromUrl(session.peer.metadata.url)
          : undefined

        const transactionData = {
          chainId,
          name: `${event.params.request.method} for ${
            session?.peer.metadata.name ??
            getDomainFromUrl(event.verifyContext.verified.origin)
          }`,
          smartAccountId: smartAccount.id,
          smartAccountAddress: smartAccount.address,
          calls: finalCalls,
          types: [BasicTransactionType.WalletConnect],
          isOfframp: false,
          walletConnectAppIcon: appIcon,
          walletConnectAppName: appName,
          walletConnectAppDomain: appDomain,
          walletConnectSessionRequestId: id,
          walletConnectSessionRequestTopic: topic,
        }

        setGlobalModalState({
          isOpen: true,
          content: (
            <AcceptWalletConnectTransactionDialogContent
              smartAccount={smartAccount}
              data={transactionData}
              appName={appName}
              appUrl={appUrl}
              requestMethod={event.params.request.method}
              onClose={() => {
                setGlobalModalState({ isOpen: false, content: null })
              }}
              onAccept={(transaction) => {
                closeGlobalModal()
                addNewRequestId(topic, id)
                respondWithTransactionId(transaction)
              }}
              onBatch={(operationId) => {
                addNewRequestId(topic, id)
                respondWithBatchResult({
                  requestId: id,
                  requestTopic: topic,
                  result: { id: operationIdToHex(operationId) },
                })
              }}
              onError={() => {
                addSentryBreadcrumb(customStringify(requestParamsMessage))
              }}
            />
          ),
          onDismiss: () => {
            respondWithCanceled(transactionData)
          },
        })
        return
      }

      if (smartAccount.type === SmartAccountType.DAIMO_FORK) {
        if (!requestParamsMessage.to) {
          toast.error('Transaction not supported at this time', {
            description:
              'Please reach out to support@splits.org with any questions',
            duration: 10000,
          })
          const response = formatJsonRpcError(id, 'Transaction not supported.')
          await web3wallet.respondSessionRequest({
            topic,
            response,
          })
          return
        }
      }

      // If there is no to field, we are deploying a contract. That means the calldata should
      // point to the smart account and it should execute the deployCreate function
      const callTo = requestParamsMessage.to
        ? requestParamsMessage.to
        : smartAccount.address
      const callValue = BigInt(requestParamsMessage.value ?? 0)
      const callData = requestParamsMessage.to
        ? (requestParamsMessage.data ?? '0x')
        : getDeployCreateData(requestParamsMessage.data)

      const transactionData = {
        chainId,
        name: `${event.params.request.method} for ${
          session?.peer.metadata.name ??
          getDomainFromUrl(event.verifyContext.verified.origin)
        }`,
        smartAccountId: smartAccount.id,
        smartAccountAddress: smartAccount.address,
        calls: [
          {
            to: callTo,
            data: callData,
            value: callValue,
          },
        ],
        types: [BasicTransactionType.WalletConnect],
        isOfframp: false,
        walletConnectAppIcon: appIcon,
        walletConnectAppName: appName,
        walletConnectAppDomain: appDomain,
        walletConnectSessionRequestId: id,
        walletConnectSessionRequestTopic: topic,
      }

      setGlobalModalState({
        isOpen: true,
        content: (
          <AcceptWalletConnectTransactionDialogContent
            smartAccount={smartAccount}
            data={transactionData}
            appName={appName}
            appUrl={appUrl}
            requestMethod={event.params.request.method}
            onClose={() => {
              setGlobalModalState({ isOpen: false, content: null })
            }}
            onAccept={(transaction) => {
              // when threshold is greater than 1 we want to unblock the dapp and generate the next
              // action without waiting for the transaction to be signed by everyone.
              closeGlobalModal()
              if (smartAccount.threshold > 1) {
                respondWithTransactionHash(transaction)
              }
              addNewRequestId(topic, id)
            }}
            onBatch={() => {
              // No JSON-RPC response — see METHOD_BATCHING_ENABLED comment
              // in useTransactionBatch.ts. addNewRequestId still runs so a
              // hard refresh doesn't reprocess.
              addNewRequestId(topic, id)
            }}
            onError={() => {
              addSentryBreadcrumb(customStringify(requestParamsMessage))
            }}
          />
        ),
        onDismiss: () => {
          respondWithCanceled(transactionData)
        },
      })
    },
    [
      web3wallet,
      smartAccountsByAddress,
      lookupRequestId,
      activeSessions,
      org,
      setGlobalModalState,
      respondWithCapabilities,
      addNewRequestId,
      respondWithCanceled,
      closeGlobalModal,
      respondWithTransactionHash,
      respondWithTransactionId,
      respondWithBatchResult,
      respondWithCallsStatus,
    ],
  )

  return {
    onSessionRequest,
    respondWithSignature,
    respondWithCanceled,
    respondWithTransactionHash,
    hasSubmittedMessage,
  }
}

type DisconnectSessionFunction = (topic: string) => Promise<void>

const useOnSessionDelete = (
  web3wallet: IWalletKit | undefined,
  removeSession: (topic: string) => void,
): {
  onSessionDelete: (event: WalletKitTypes.SessionDelete) => Promise<void>
  disconnectFromDapp: DisconnectSessionFunction
} => {
  const disconnectFromDapp = useCallback(
    async (topic: string) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      await web3wallet.disconnectSession({
        topic,
        reason: getSdkError('USER_DISCONNECTED'),
      })
      removeSession(topic)
    },
    [web3wallet, removeSession],
  )

  const onSessionDelete = useCallback(
    async (event: WalletKitTypes.SessionDelete) => {
      removeSession(event.topic)
    },
    [removeSession],
  )

  return { disconnectFromDapp, onSessionDelete }
}

const MAX_WALLET_CONNECT_REQUEST_STORAGE_TIME = 60 * 30 * 1000 // 30 minutes (in milliseconds)
// This hook prevents processing the same request twice
const useStoreRequestIds = () => {
  const [requestIdData, setRequestIdData] = useLocalStorage<{
    [key: string]: number
  }>('splits.smartAccounts.walletConnect.requestIds', {})

  const getFilteredRequestIdData = useCallback(() => {
    const currentTime = Date.now()

    return omitBy(
      requestIdData ?? {},
      (reqTime) =>
        currentTime - reqTime > MAX_WALLET_CONNECT_REQUEST_STORAGE_TIME,
    )
  }, [requestIdData])

  const addNewRequestId = useCallback(
    (topic: string, id: number) => {
      const startingData = getFilteredRequestIdData() // don't let data grow indefinitely, purge after 30 minutes
      startingData[`${topic}-${id}`] = Date.now()
      setRequestIdData(startingData)
    },
    [getFilteredRequestIdData, setRequestIdData],
  )

  const lookupRequestId = useCallback(
    (topic: string, id: number) => {
      const key = `${topic}-${id}`
      return requestIdData?.[key] !== undefined
    },
    [requestIdData],
  )

  return {
    addNewRequestId,
    lookupRequestId,
  }
}

const MAX_MESSAGE_STORAGE_TIME = 60 * 60 * 1000 // 1 hour (in milliseconds)
// This hook prevents submitting the same message twice
const useStoreSubmittedMessages = () => {
  const [messageData, setMessageData] = useLocalStorage<{
    [key: string]: number
  }>('splits.smartAccounts.messages.submittedIds', {})

  const getFilteredMessageData = useCallback(() => {
    const currentTime = Date.now()

    return omitBy(
      messageData ?? {},
      (reqTime) => currentTime - reqTime > MAX_MESSAGE_STORAGE_TIME,
    )
  }, [messageData])

  const addNewMessageId = useCallback(
    (messageId: string) => {
      const startingData = getFilteredMessageData() // don't let data grow indefinitely, purge after 1 hour
      startingData[messageId] = Date.now()
      setMessageData(startingData)
    },
    [getFilteredMessageData, setMessageData],
  )

  const lookupMessageId = useCallback(
    (messageId: string) => {
      return messageData?.[messageId] !== undefined
    },
    [messageData],
  )

  return {
    addNewMessageId,
    lookupMessageId,
  }
}

export type WalletConnectControls = {
  pendingProposal: WalletKitTypes.SessionProposal | undefined
  isApprovingSession: boolean
  isPairing: boolean
  activeSessions: SessionTypes.Struct[]
  approveSession: ApproveSessionFunction
  rejectSession: RejectSessionFunction
  pairToDapp: (uri: string) => Promise<void>
  disconnectSession: (topic: string) => Promise<void>
  respondWithSignature: (message: OrgMessage) => Promise<void>
  respondWithTransactionHash: (transaction: OrgTransaction) => Promise<void>
  respondWithCanceled: (
    data: CreateMessageData | CreateTransactionData,
  ) => Promise<void>
  hasSubmittedMessage: (messageId: string) => boolean
}

export const useWalletConnect = (): WalletConnectControls => {
  const { web3wallet, initWalletConnect } = useWeb3Wallet()
  useEffect(() => {
    initWalletConnect()
  }, [initWalletConnect])

  const {
    approveSession,
    rejectSession,
    removeSession,
    onSessionProposal,
    addSession,
    setIsPairing,
    isApproving,
    pendingProposal,
    activeSessions,
    isPairing,
  } = useSessionProposal(web3wallet)
  useEffect(() => {
    if (!web3wallet) return
    web3wallet.on('session_proposal', onSessionProposal)

    return () => {
      web3wallet.off('session_proposal', onSessionProposal)
    }
  }, [web3wallet, onSessionProposal])

  const {
    onSessionRequest,
    respondWithSignature,
    respondWithCanceled,
    respondWithTransactionHash,
    hasSubmittedMessage,
  } = useOnSessionRequest(web3wallet, activeSessions)
  useEffect(() => {
    if (!web3wallet) return
    web3wallet.on('session_request', onSessionRequest)

    return () => {
      web3wallet.off('session_request', onSessionRequest)
    }
  }, [web3wallet, onSessionRequest])

  const { disconnectFromDapp, onSessionDelete } = useOnSessionDelete(
    web3wallet,
    removeSession,
  )
  useEffect(() => {
    if (!web3wallet) return
    web3wallet.on('session_delete', onSessionDelete)

    return () => {
      web3wallet.off('session_delete', onSessionDelete)
    }
  }, [web3wallet, onSessionDelete])

  const disconnectSession = useCallback(
    async (topic: string) => {
      await disconnectFromDapp(topic)
    },
    [disconnectFromDapp],
  )

  const pairToDapp = useCallback(
    async (uri: string) => {
      if (!web3wallet)
        throw new Error('Must wait for web3wallet initialization')

      setIsPairing(true)
      try {
        await web3wallet.pair({ uri })
      } catch (error) {
        const errorMessage = error.message ?? ''
        let toastMessage
        if (errorMessage.startsWith('Pairing already exists')) {
          toastMessage = 'Wallet connect URI pairing already exists.'
        } else if (errorMessage.startsWith('Missing or invalid')) {
          toastMessage = 'Invalid Wallet connect URI.'
        } else if (errorMessage.startsWith('Expired')) {
          toastMessage = 'Wallet connect URI has expired.'
        } else {
          toastMessage = 'Failed to connect using Wallet connect.'
        }
        toast.error(toastMessage)
      } finally {
        setIsPairing(false)
      }
    },
    [web3wallet, setIsPairing],
  )

  // Reload active sessions into state on first load
  useEffect(() => {
    if (!web3wallet) return

    const result = web3wallet.getActiveSessions()
    Object.values(result).map((session) =>
      addSession(session as SessionTypes.Struct),
    )
  }, [web3wallet, addSession])

  return {
    pendingProposal,
    isApprovingSession: isApproving,
    isPairing,
    activeSessions,
    approveSession,
    rejectSession,
    pairToDapp,
    disconnectSession,
    respondWithSignature,
    respondWithCanceled,
    respondWithTransactionHash,
    hasSubmittedMessage,
  }
}
